import {
  addressSchema,
  pictureSchema,
  signatureUrlSchema,
} from '@omecare/utils';
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { EditUserProfileFormValues } from '@/components/forms/edit-user-profile/schema';
import http from '@/utils/http.service';

const editAttendingProfileResponseSchema = z.object({
  id: z.string().uuid(),
  firstname: z.string(),
  phoneNumber: z.string(),
  lastname: z.string(),
  picture: pictureSchema,
  address: addressSchema.nullable(),
  orderRegistrationNumber: z.string(),
  rppsNumber: z.string().nullable().optional(),
  signatureUrl: signatureUrlSchema,
  dateOfBirth: z.coerce.date().nullable(),
  thesis: z.boolean(),
  // Structured rhythm fields
  onSiteSlotsWithoutAssistant: z.number().int().nullable().default(null),
  remoteSlotsWithoutAssistant: z.number().int().nullable().default(null),
  onSiteSlotsWithAssistant: z.number().int().nullable().default(null),
  remoteSlotsWithAssistant: z.number().int().nullable().default(null),
  iban: z.string(),
  bic: z.string().nullable().default(null),
  createdAt: z.coerce.date(),
});

export type EditAttendingProfileResponse = z.infer<
  typeof editAttendingProfileResponseSchema
>;
export type EditAttendingProfilePayload = {
  thesis: boolean;
  // Structured rhythm fields
  onSiteSlotsWithoutAssistant?: number | null;
  remoteSlotsWithoutAssistant?: number | null;
  onSiteSlotsWithAssistant?: number | null;
  remoteSlotsWithAssistant?: number | null;
  deleteAvatar: boolean;
} & Omit<EditUserProfileFormValues, 'picture' | 'thesis' | 'signatureUrl'>;

const editAttendingProfile = (payload: EditAttendingProfilePayload) => {
  return http.users
    .updateProfile({
      firstname: payload.firstname,
      lastname: payload.lastname,
      phoneNumber: payload.phoneNumber,
      dateOfBirth: payload.dateOfBirth,
      orderRegistrationNumber: payload.orderRegistrationNumber,
      rppsNumber: payload.rppsNumber,
      iban: payload.iban,
      bic: payload.bic,
      thesis: payload.thesis,
      onSiteSlotsWithoutAssistant: payload.onSiteSlotsWithoutAssistant,
      remoteSlotsWithoutAssistant: payload.remoteSlotsWithoutAssistant,
      onSiteSlotsWithAssistant: payload.onSiteSlotsWithAssistant,
      remoteSlotsWithAssistant: payload.remoteSlotsWithAssistant,
      file: payload.file,
      deleteAvatar: payload.deleteAvatar,
      signatureFile: payload.signatureFile ?? undefined,
      address: payload.address
        ? {
            ...payload.address,
            state: 'France',
          }
        : undefined,
    })
    .then<EditAttendingProfileResponse>(({ response, transform }) => {
      const user = transform.handle({
        response,
        attributes: {
          firstname: true,
          phoneNumber: true,
          lastname: true,
          picture: true,
          createdAt: true,
        },
        relations: {
          doctorAttending: true,
        },
      });

      return editAttendingProfileResponseSchema.parse({
        id: user.id,
        firstname: user.firstname,
        phoneNumber: user.phoneNumber,
        lastname: user.lastname,
        picture: user.picture,
        address: user.doctorAttending?.address ?? null,
        orderRegistrationNumber:
          user.doctorAttending?.orderRegistrationNumber ?? '',
        rppsNumber: user.doctorAttending?.rppsNumber,
        signatureUrl: user.doctorAttending?.signatureUrl,
        dateOfBirth: user.doctorAttending?.dateOfBirth,
        thesis: user.doctorAttending?.thesis ?? false,
        onSiteSlotsWithoutAssistant:
          user.doctorAttending?.onSiteSlotsWithoutAssistant,
        remoteSlotsWithoutAssistant:
          user.doctorAttending?.remoteSlotsWithoutAssistant,
        onSiteSlotsWithAssistant:
          user.doctorAttending?.onSiteSlotsWithAssistant,
        remoteSlotsWithAssistant:
          user.doctorAttending?.remoteSlotsWithAssistant,
        iban: user.doctorAttending?.iban ?? '',
        bic: user.doctorAttending?.bic,
        createdAt: user.createdAt,
      });
    });
};

const useEditAttendingProfileMutation = (): UseMutationResult<
  EditAttendingProfileResponse,
  Error,
  EditAttendingProfilePayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editAttendingProfile,
    onSuccess() {
      toast.success(t('http.edit-profile.success'));
      queryClient.invalidateQueries({
        queryKey: ['me'],
      });
    },
  });
};

export default useEditAttendingProfileMutation;
